import { createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ArrowLeft, ImagePlus, Package, Star, X } from "lucide-solid";

import { Link, useNavigate } from "@/lib/u-router";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";
import { createPostMutation, MAX_POST_MEDIA, validatePostDraft } from "@famidex/shared";

/**
 * 📣 Crear nueva publicación — compositor (foto por URL + pie + etiqueta de
 * producto + valoración). Publicar requiere cuenta (guest → login). Traducción
 * del diseño Stitch `crear_nueva_publicacion`. El upload de cámara/archivo es un
 * siguiente paso; el MVP acepta imágenes por URL y publicaciones sólo-texto.
 */
export function CreatePostPage(props: { initialProductId?: string }) {
  const navigate = useNavigate();
  const session = useSession();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const [caption, setCaption] = createSignal("");
  const [media, setMedia] = createSignal<string[]>([]);
  const [mediaInput, setMediaInput] = createSignal("");
  const [rating, setRating] = createSignal<number | null>(null);
  const [productId, setProductId] = createSignal<string | null>(props.initialProductId ?? null);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const addMedia = () => {
    const url = mediaInput().trim();
    if (!url || media().length >= MAX_POST_MEDIA) {
      return;
    }
    setMedia((prev) => [...prev, url]);
    setMediaInput("");
  };

  const removeMedia = (i: number) => {
    setMedia((prev) => prev.filter((_, idx) => idx !== i));
  };

  const errorLabel = (code: string): string => {
    switch (code) {
      case "empty": {
        return t(msg`Escribe un pie de foto o añade una imagen.`);
      }
      case "too_many_media": {
        return t(msg`Máximo ${MAX_POST_MEDIA} imágenes.`);
      }
      case "caption_too_long": {
        return t(msg`El pie de foto es demasiado largo.`);
      }
      default: {
        return t(msg`Revisa los datos de la publicación.`);
      }
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (!session.isAuthenticated()) {
      void navigate({ to: "/login" });
      return;
    }
    const draft = {
      caption: caption(),
      media: media(),
      productId: productId(),
      rating: rating(),
    };
    const validation = validatePostDraft(draft);
    if (!validation.ok) {
      setError(errorLabel(validation.errors[0] ?? ""));
      return;
    }
    const db = drizzle();
    const uid = session.userId();
    if (!db || !uid) {
      return;
    }
    setBusy(true);
    try {
      await createPostMutation(
        db,
        uid,
        { name: session.data?.user?.name ?? null, image: session.data?.user?.image ?? null },
        draft,
      );
      void navigate({ to: "/community" });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="flex flex-col gap-6" data-testid="create-post">
      <header class="flex items-center justify-between">
        <Link
          to="/community"
          class="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft class="h-4 w-4" />
          {t(msg`Comunidad`)}
        </Link>
        <button
          type="button"
          data-testid="publish-post"
          disabled={busy()}
          onClick={() => void handleSubmit()}
          class="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60"
        >
          {t(msg`Compartir`)}
        </button>
      </header>

      <h1 class="text-2xl font-semibold text-foreground">{t(msg`Nueva publicación`)}</h1>

      {/* Media */}
      <section class="flex flex-col gap-3 rounded-xl border border-border/60 bg-card/70 p-4">
        <Show when={media().length > 0}>
          <div class="flex gap-2 overflow-x-auto pb-1">
            <For each={media()}>
              {(url, i) => (
                <div class="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-muted">
                  <img src={url} alt="" class="h-full w-full object-cover" />
                  <button
                    type="button"
                    aria-label={t(msg`Quitar imagen`)}
                    onClick={() => {
                      removeMedia(i());
                    }}
                    class="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-background/80 text-destructive"
                  >
                    <X class="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </For>
          </div>
        </Show>
        <Show when={media().length < MAX_POST_MEDIA}>
          <div class="flex items-center gap-2">
            <input
              type="url"
              inputmode="url"
              data-testid="media-url"
              value={mediaInput()}
              onInput={(e) => setMediaInput(e.currentTarget.value)}
              placeholder={t(msg`Pega la URL de una foto`)}
              class="min-w-0 flex-1 rounded-xl border border-border/60 bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="button"
              onClick={addMedia}
              class="inline-flex items-center gap-1 rounded-xl border-2 border-dashed border-border px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:border-primary hover:text-primary"
            >
              <ImagePlus class="h-4 w-4" />
              {t(msg`Añadir`)}
            </button>
          </div>
        </Show>
      </section>

      {/* Pie de foto */}
      <textarea
        data-testid="post-caption"
        value={caption()}
        onInput={(e) => setCaption(e.currentTarget.value)}
        placeholder={t(msg`Escribe un pie de foto...`)}
        class="min-h-[120px] w-full resize-none rounded-xl border border-border/60 bg-card/70 p-4 text-sm outline-none focus:ring-2 focus:ring-primary"
      />

      {/* Etiqueta de producto (opcional) */}
      <Show when={productId()}>
        {(pid) => (
          <div class="flex items-center justify-between rounded-xl border border-border/60 bg-accent/40 p-4">
            <span class="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
              <Package class="h-4 w-4 text-primary" />
              {t(msg`Set etiquetado`)}: {pid()}
            </span>
            <button
              type="button"
              onClick={() => setProductId(null)}
              class="text-muted-foreground hover:text-destructive"
              aria-label={t(msg`Quitar etiqueta`)}
            >
              <X class="h-4 w-4" />
            </button>
          </div>
        )}
      </Show>

      {/* Valoración */}
      <section class="flex items-center justify-between rounded-xl border border-border/60 bg-card/70 p-4">
        <span class="text-sm font-semibold text-foreground">{t(msg`Califica este set`)}</span>
        <div class="flex gap-1" data-testid="rating">
          <For each={[1, 2, 3, 4, 5]}>
            {(n) => (
              <button
                type="button"
                aria-label={t(msg`${n} estrellas`)}
                onClick={() => setRating((prev) => (prev === n ? null : n))}
              >
                <Star
                  class="h-6 w-6 transition-colors"
                  classList={{
                    "fill-primary text-primary": (rating() ?? 0) >= n,
                    "text-muted-foreground/40": (rating() ?? 0) < n,
                  }}
                />
              </button>
            )}
          </For>
        </div>
      </section>

      <Show when={error()}>
        <p data-testid="post-error" class="text-center text-sm text-destructive">
          {error()}
        </p>
      </Show>
    </div>
  );
}
